import {
  Box,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import { tokens } from '../../../global/theme/tokens';
import ExplorerFilePreview from './ExplorerFilePreview';
import { formatBytes } from '../explorer/MediaInfo';
import {
  entryKind,
  entryLabel,
  FOLDER_COLOR,
  folderPurposes,
  listingToEntries,
} from './explorerUtils';

// cap the visible depth so deep paths don't force sideways scrolling,
// especially on tablet/mobile; the breadcrumb still reaches hidden ancestors
const MAX_VISIBLE_COLUMNS = 4;

const ExplorerColumnView = ({
  columns,
  query,
  selected,
  onOpenDir,
  onSelectFile,
  onOpen,
  onContextMenu,
  preview,
  onDownload,
  renaming,
  onRenamingChange,
  onRenameCommit,
  onRenameCancel,
  newFolderName,
  onNewFolderChange,
  onNewFolderCommit,
  onNewFolderCancel,
  dnd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const trimmedQuery = query.trim().toLowerCase();
  const showPreview = selected && !selected.isDir;
  const selectedKind = selected ? entryKind(selected) : null;

  const hiddenColumnCount = Math.max(0, columns.length - MAX_VISIBLE_COLUMNS);
  const visibleColumns = columns.slice(hiddenColumnCount);

  return (
    <Box className="flex-1 min-h-0 flex flex-row overflow-x-auto">
      {visibleColumns.map((column, visibleIndex) => {
        if (!column) return null;
        // index within the full columns array - the parent truncates by it
        const columnIndex = hiddenColumnCount + visibleIndex;
        let entries = listingToEntries(column);
        // only filter the deepest column - the query targets the cwd
        if (trimmedQuery && columnIndex === columns.length - 1)
          entries = entries.filter((entry) =>
            entryLabel(entry).toLowerCase().includes(trimmedQuery),
          );
        const isColumnDropTarget =
          !dnd.dragPath && dnd.dragOverPath === column.path;
        return (
          <Box
            key={column.path || 'root'}
            className="flex-none w-60 h-full overflow-y-auto p-1"
            sx={{
              borderRight: `1px solid ${colors.glass}`,
              backgroundColor: isColumnDropTarget
                ? colors.info + '15'
                : 'transparent',
              boxShadow: isColumnDropTarget
                ? `inset 0 0 0 2px ${colors.info}`
                : 'none',
            }}
            // OS file drags over the column (not over a folder row) upload
            // into the column's own folder
            onDragOver={(event) => {
              if (!dnd.dragPath && dnd.isFileDrag(event)) {
                event.preventDefault();
                event.stopPropagation();
                dnd.fileOver(column.path);
              }
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                dnd.leave(column.path);
            }}
            onDrop={(event) => {
              if (!dnd.dragPath && event.dataTransfer?.files?.length) {
                event.preventDefault();
                event.stopPropagation();
                dnd.fileDrop(column.path, event.dataTransfer.files);
              }
            }}
          >
            {newFolderName !== null && columnIndex === columns.length - 1 && (
              <Box
                className="flex flex-row items-center gap-2 px-2 py-1 rounded-lg"
                sx={{ backgroundColor: colors.bgInput }}
              >
                <FolderIcon sx={{ fontSize: '19px', color: FOLDER_COLOR }} />
                <TextField
                  size="small"
                  autoFocus
                  value={newFolderName}
                  placeholder={t('New Folder')}
                  onChange={(event) => onNewFolderChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') onNewFolderCommit();
                    if (event.key === 'Escape') onNewFolderCancel();
                  }}
                  onBlur={onNewFolderCommit}
                />
              </Box>
            )}
            {entries.map((entry) => {
              const kind = entryKind(entry);
              const purposes = folderPurposes(entry);
              const isSelected = selected?.path === entry.path;
              const isInPath = columns.some(
                (col) => col && col.path === entry.path,
              );
              const isDragTarget =
                dnd.dragOverPath === entry.path && entry.isDir;
              return (
                <Box
                  key={entry.path}
                  draggable={dnd.canEdit}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (entry.isDir) onOpenDir(entry, columnIndex);
                    else onSelectFile(entry, columnIndex);
                  }}
                  onDoubleClick={() => onOpen(entry)}
                  onContextMenu={(event) => onContextMenu(event, entry)}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    dnd.start(entry.path);
                  }}
                  onDragEnd={dnd.end}
                  onDragOver={(event) => {
                    if (!entry.isDir) return;
                    if (dnd.dragPath && dnd.dragPath !== entry.path) {
                      event.preventDefault();
                      event.stopPropagation();
                      dnd.over(entry.path);
                    } else if (!dnd.dragPath && dnd.isFileDrag(event)) {
                      event.preventDefault();
                      event.stopPropagation();
                      dnd.fileOver(entry.path);
                    }
                  }}
                  onDragLeave={() => dnd.leave(entry.path)}
                  onDrop={(event) => {
                    if (!entry.isDir) return;
                    if (dnd.dragPath) {
                      event.preventDefault();
                      event.stopPropagation();
                      dnd.dropOn(entry.path);
                    } else if (event.dataTransfer?.files?.length) {
                      event.preventDefault();
                      event.stopPropagation();
                      dnd.fileDrop(entry.path, event.dataTransfer.files);
                    }
                  }}
                  className="flex flex-row items-center gap-2 px-2 rounded-lg cursor-default"
                  sx={{
                    height: '32px',
                    backgroundColor: isDragTarget
                      ? colors.info + '30'
                      : isSelected
                        ? colors.info + '30'
                        : isInPath
                          ? colors.info + '15'
                          : 'transparent',
                    outline: isDragTarget ? `2px solid ${colors.info}` : 'none',
                    ':hover': {
                      backgroundColor: isSelected
                        ? colors.info + '40'
                        : isInPath
                          ? colors.info + '25'
                          : colors.bgInput,
                    },
                  }}
                >
                  <kind.Icon sx={{ fontSize: '19px', color: kind.color }} />
                  {renaming?.entry?.path === entry.path ? (
                    <TextField
                      size="small"
                      autoFocus
                      value={renaming.value}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => onRenamingChange(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') onRenameCommit();
                        if (event.key === 'Escape') onRenameCancel();
                      }}
                      onBlur={onRenameCommit}
                    />
                  ) : (
                    <Typography variant="body2" className="flex-1 truncate">
                      {entryLabel(entry)}
                    </Typography>
                  )}
                  {purposes.map((purpose) => (
                    <Tooltip key={purpose.key} title={t(purpose.label)}>
                      <purpose.Icon sx={{ fontSize: '15px', opacity: 0.6 }} />
                    </Tooltip>
                  ))}
                  {entry.isDir && (
                    <ChevronRightIcon
                      sx={{ fontSize: '16px', opacity: 0.4 }}
                    />
                  )}
                </Box>
              );
            })}
            {entries.length === 0 && (
              <Typography
                variant="caption"
                className="block text-center py-10 opacity-50"
              >
                {t('Empty folder')}
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Preview panel for the selected file */}
      {showPreview && (
        <Box
          className="flex-1 min-w-0 h-full flex flex-col"
          sx={{ backgroundColor: colors.paper, minWidth: '260px' }}
        >
          <Box
            className="flex flex-row items-center gap-2 px-3 py-2"
            sx={{ borderBottom: `1px solid ${colors.glass}` }}
          >
            <selectedKind.Icon
              sx={{ fontSize: '18px', color: selectedKind.color }}
            />
            <Box className="min-w-0 flex-1">
              <Typography variant="body2" className="truncate" sx={{ fontWeight: 500 }}>
                {entryLabel(selected)}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                {t(selectedKind.label)}
                {selected.size ? ` · ${formatBytes(selected.size)}` : ''}
              </Typography>
            </Box>
            <IconButton
              size="small"
              title={t('Download')}
              onClick={() => onDownload(selected)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box className="flex-1 min-h-0 overflow-hidden">
            {preview && preview.path === selected.path ? (
              <ExplorerFilePreview
                src={preview.url}
                mimeType={preview.mimeType || selected.mimeType || ''}
                entry={selected}
              />
            ) : (
              <Box className="w-full h-full flex items-center justify-center">
                <Typography variant="caption" sx={{ opacity: 0.5 }}>
                  {t('Loading')}...
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ExplorerColumnView;
